// Validators.js
import { loadGoogleMaps } from '../googleMapsLoader'; // Adjust path to your loader utility

/**
 * Method to retrieve city information from a string.
 *
 * @param {string} cityString - City to validate and retrieve data for.
 *
 * @returns {Promise<object|null>} - Object with city data if valid, null if invalid.
 */
export const getCity = async (cityString) => {
  // Check if the input is a non-empty string
  if (!cityString || typeof cityString !== 'string' || cityString.trim() === '') {
    return null;
  }

  try {
    // Load Google Maps API
    const google = await loadGoogleMaps();

    // Create a new Geocoder instance
    const geocoder = new google.maps.Geocoder();

    // Return a Promise to handle the asynchronous geocode request
    return new Promise((resolve) => {
      geocoder.geocode({ address: cityString }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          // Find the first result with 'locality' type
          const cityResult = results.find((result) =>
            result.types.includes('locality')
          );

          if (cityResult) {
            // Extract city data
            let city = '';
            let county = '';
            let state = '';
            let country = '';
            let latitude = cityResult.geometry.location.lat();
            let longitude = cityResult.geometry.location.lng();
            let placeId = cityResult.place_id;

            cityResult.address_components.forEach((component) => {
              const types = component.types;
              if (types.includes('locality')) {
                city = component.long_name; // e.g., "Glasgow"
              } else if (types.includes('administrative_area_level_2')) {
                county = component.long_name; // e.g., "Valley County"
              } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name; // e.g., "MT"
              } else if (types.includes('country')) {
                country = component.short_name; // e.g., "US"
              }
            });

            // Return the extracted data as an object
            resolve({
              city,
              county,
              state,
              country,
              latitude,
              longitude,
              placeId,
            });
          } else {
            // No result with 'locality' type found
            resolve(null);
          }
        } else {
          // Status not OK or no results
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error('Error retrieving city data:', error);
    return null;
  }
};

/**
 * Method to validate email format.
 * 
 * @param {string} email - Email to validate.
 * 
 * @returns {boolean} - Whether the email is valid.
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple email regex for validation
  return emailRegex.test(email);
};

/**
 * Checks whether a password is valid based on defined requirements.
 *
 * Requirements:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character (!@#$%^&*)
 *
 * @param {string} password - The password string to validate.
 * @returns {boolean} True if the password is valid, false otherwise.
 */
export const isValidPassword = (password) => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*]/.test(password)
  );
};

/**
 * Checks whether a user-facing display name is valid.
 *
 * @param {string} name - Name to validate.
 * @returns {boolean} True when the name contains non-whitespace characters.
 */
export const isValidUserName = (name) => {
  return String(name || '').trim().length > 0;
};
